import AutoAwesomeIcon from '@material-design-icons/svg/filled/auto_awesome.svg?react';

import { Button } from '../../../components/ui/button';
import type { Candidate, CandidateRun } from '../types';
import { packageKey } from '../utils';
import { CandidateCard } from './CandidateCard';
import { Section } from './Section';

interface CandidateSuggestionsProps {
  weekId: string;
  candidateRuns: CandidateRun[];
  selectedPackages: Set<string>;
  canAddApp: boolean;
  isGenerating: boolean;
  readOnly: boolean;
  onGenerate: () => void;
  onAddCandidate: (candidate: Candidate) => void;
}

export function CandidateSuggestions({
  weekId,
  candidateRuns,
  selectedPackages,
  canAddApp,
  isGenerating,
  readOnly,
  onGenerate,
  onAddCandidate,
}: CandidateSuggestionsProps) {
  const run =
    candidateRuns.find((item) => item.weekId === weekId) ??
    candidateRuns[0] ??
    null;

  return (
    <Section
      title="Suggestions"
      icon={<AutoAwesomeIcon className="size-4 fill-current" />}
      actions={
        readOnly ? null : (
          <Button
            variant="outline"
            size="sm"
            onClick={onGenerate}
            disabled={isGenerating}
          >
            <AutoAwesomeIcon className="size-4 fill-current" />
            {isGenerating ? 'Generating…' : 'Generate'}
          </Button>
        )
      }
    >
      {!run ? (
        <p className="text-sm text-neutral-500">
          No suggestions yet. Generate an editorial candidate set for{' '}
          <span className="text-neutral-300">{weekId}</span>.
        </p>
      ) : (
        <>
          <p className="mb-3 text-xs text-neutral-500">
            {run.generatedCandidates.length} candidates for{' '}
            <span className="text-neutral-300">{run.weekId}</span>
            {run.weekId !== weekId && ' (most recent run)'} · {run.modelVersion}
          </p>
          {run.generatedCandidates.length === 0 ? (
            <p className="text-sm text-neutral-500">
              This run produced no candidates.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {run.generatedCandidates.map((candidate) => {
                const isAdded = selectedPackages.has(
                  packageKey(candidate.androidPackage),
                );
                return (
                  <CandidateCard
                    key={candidate.androidPackage}
                    candidate={candidate}
                    isAdded={isAdded}
                    canAdd={!readOnly && canAddApp && !isAdded}
                    onAdd={() => onAddCandidate(candidate)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </Section>
  );
}
